import React, { useState } from 'react';
import styled from 'styled-components';
import { Section, Container, BlockTitle } from '../styles/shared';
import { resolveGalleryImageSrc } from '../utils/mediaServeUrls';
import { resolvePresentation } from '../utils/blockPresentation';

const Grid = styled.div`
  display: grid;
  grid-template-columns: ${({ $cols }) => `repeat(${$cols}, 1fr)`};
  gap: ${({ $gap }) => $gap === 'small' ? '8px' : $gap === 'large' ? '24px' : '16px'};
  @media (max-width: 768px)  { grid-template-columns: repeat(2, 1fr) !important; }
  @media (max-width: 480px)  { grid-template-columns: 1fr !important; }
`;

const Item = styled.div`
  position: relative; overflow: hidden; border-radius: ${({ $fullBleed }) => ($fullBleed ? '0' : '8px')};
  cursor: pointer; transition: transform 0.2s ease;
  ${({ $ratio, $fixedHeight }) => !$fixedHeight && $ratio === '1:1' && 'aspect-ratio: 1 / 1;'}
  ${({ $ratio, $fixedHeight }) => !$fixedHeight && $ratio === '4:3' && 'aspect-ratio: 4 / 3;'}
  ${({ $ratio, $fixedHeight }) => !$fixedHeight && $ratio === '16:9' && 'aspect-ratio: 16 / 9;'}
  ${({ $fixedHeight }) => $fixedHeight && `height: ${$fixedHeight};`}
  &:hover { transform: scale(1.02); }
  img {
    width: 100%;
    height: 100%;
    object-fit: ${({ $objectFit }) => $objectFit || 'cover'};
    ${({ $objectPosition }) => $objectPosition && `object-position: ${$objectPosition};`}
    display: block;
  }
`;

const Caption = styled.div`
  position: absolute; bottom: 0; left: 0; right: 0; padding: 12px;
  background: linear-gradient(transparent, rgba(0,0,0,0.7));
  color: #fff; font-size: 0.875rem;
`;

const Lightbox = styled.div`
  position: fixed; top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.95); z-index: 9999;
  display: flex; align-items: center; justify-content: center; padding: 40px;
  img { max-width: 100%; max-height: 100%; object-fit: contain; }
`;

const LightboxClose = styled.button`
  position: absolute; top: 20px; right: 20px;
  background: none; border: none; color: #fff;
  font-size: 2rem; cursor: pointer; padding: 10px; line-height: 1;
`;

const GalleryBlock = ({ content = {}, settings = {}, tenantSlug, apiBase }) => {
  const {
    title = '', images = [], columns = 3,
    gap = 'medium', aspectRatio = '4:3'
  } = content;
  const { backgroundColor = '', padding = 'large' } = settings;
  // defaultFit cover matches today's Gallery CSS — same pixels when unset.
  const presentation = resolvePresentation(settings, {
    defaultFit: 'cover',
    defaultPosition: null,
    defaultHeight: null,
  });
  const [lightbox, setLightbox] = useState(null);
  const fixedHeight = presentation.imageHeight && presentation.imageHeight !== 'auto'
    ? presentation.imageHeight
    : null;

  return (
    <Section $padding={padding} $bgColor={backgroundColor} $fullBleed={presentation.fullBleed}>
      <Container $fullBleed={presentation.fullBleed}>
        {title && <BlockTitle style={{ textAlign: 'center' }}>{title}</BlockTitle>}
        <Grid $cols={columns} $gap={gap}>
          {images.map((img, i) => (
            <Item
              key={i}
              $ratio={aspectRatio}
              $fixedHeight={fixedHeight}
              $objectFit={presentation.objectFit}
              $objectPosition={presentation.objectPosition}
              $fullBleed={presentation.fullBleed}
              onClick={() => setLightbox(img)}
            >
              <img src={resolveGalleryImageSrc(img, tenantSlug, apiBase)} alt={img.alt || `Gallery ${i + 1}`} loading="lazy" />
              {img.caption && <Caption>{img.caption}</Caption>}
            </Item>
          ))}
        </Grid>
      </Container>
      {lightbox && (
        <Lightbox onClick={() => setLightbox(null)}>
          <LightboxClose onClick={() => setLightbox(null)}>×</LightboxClose>
          <img src={resolveGalleryImageSrc(lightbox, tenantSlug, apiBase)} alt={lightbox.alt || ''} />
        </Lightbox>
      )}
    </Section>
  );
};

export default GalleryBlock;
